import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Paper, Skeleton, Switch, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'

import {
  getBaselineScope, listBaselineVersions, publishBaseline, rollbackBaseline, updateBaselineDraft,
  type BaselineScopeDTO, type BaselineVersionDTO,
} from '@/services/riskBaselineAdminService'
import { listClauseTypes, type ClauseTypeAdminDTO } from '@/services/taxonomyAdminService'
import type { BaselineConfig } from '@/lib/riskBaselineConfig'
import { diffBaselineConfigs, parseBaselineConfig, summarizeBaselineDiff, BASELINE_DEFAULTS } from '@/lib/riskConfigDiff'
import { useAdminScope } from '../-admin/adminScope'
import { BaselineTab } from '../-risk/BaselineTab'
import { LiveExampleRail } from '../-risk/LiveExampleRail'
import { RiskPolicyTab } from '../-risk/RiskPolicyTab'
import { RiskVersionsTab } from '../-risk/RiskVersionsTab'
import { PageHeader } from '@/components/page/PageHeader'
import { PublishBar, PublishDialog, type PublishChangeItem } from '../-admin/PublishBar'
import type { BaselineSection } from '@/lib/riskConfigDiff'
import '@/styles/pages.css'
import '@/styles/risk-admin.css'

type RiskTabId = 'baseline' | 'ai-policy' | 'versions'

/** What each baseline section is called in the publish dialog's change list. */
const SECTION_LABEL: Record<BaselineSection, string> = {
  scoring: 'Scoring',
  gapRules: 'Gap rule',
  clauseTypeRules: 'Floor rule',
  detailRules: 'Detail rule',
}

const TAB_IDS: RiskTabId[] = ['baseline', 'ai-policy', 'versions']

export const Route = createFileRoute('/_app/organization/admin/risk')({
  head: () => ({ meta: [{ title: 'Risk · Admin · AERRIS' }] }),
  validateSearch: (search: Record<string, unknown>): { tab: RiskTabId } => ({
    tab: TAB_IDS.includes(search.tab as RiskTabId) ? (search.tab as RiskTabId) : 'baseline',
  }),
  component: RiskConfigPage,
})

/**
 * Risk configuration (docs/plans/risk-config-redesign.md) — the three-tab admin for ADR 0023's
 * two layers: the deterministic baseline (tab 1, structured builders + JSON escape hatch + live
 * example rail), the AI policy (tab 2, the risk_policy prompt + model config), and the
 * append-only Versions history (tab 3). Sub-tabs are deep-linkable via ?tab= (D5); all three
 * panels stay mounted so switching tabs never loses unsaved edits.
 */
function RiskConfigPage() {
  const { customerUuid, globalMode, clientId, canManage, scopeName, loading: scopeLoading } = useAdminScope()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()

  const [scope, setScope] = useState<BaselineScopeDTO | null>(null)
  const [versions, setVersions] = useState<BaselineVersionDTO[]>([])
  const [config, setConfig] = useState<BaselineConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  // The publish confirm dialog (Authorized LLM Spend, CONTEXT-MAP.md): publishing never spends
  // LLM tokens by itself — the scope re-evaluation runs only when this toggle is ticked, and the
  // toggle resets to OFF on every open so yesterday's authorization never carries over.
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
  const [reevaluateOnPublish, setReevaluateOnPublish] = useState(false)
  const [clauseTypes, setClauseTypes] = useState<ClauseTypeAdminDTO[] | null>(null)

  const setTab = (next: RiskTabId) => {
    void navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }), replace: true })
  }

  const load = useCallback(async () => {
    if (scopeLoading) return
    setLoading(true)
    try {
      if (!canManage || (!globalMode && !clientId)) {
        setScope(null)
        setConfig(null)
        return
      }
      const [s, vs] = await Promise.all([
        getBaselineScope(customerUuid),
        listBaselineVersions(customerUuid),
      ])
      setScope(s)
      setVersions(vs)
      // Seed the editor from the draft when one exists, else from the effective (possibly
      // global-fallback) published config — editing then forks the scope's own draft.
      setConfig(parseBaselineConfig(s.draftConfig ?? s.effectiveConfig) ?? BASELINE_DEFAULTS)
      setDirty(false)
    } catch (e) {
      console.error('Failed to load risk configuration:', e)
      notifications.show({ color: 'red', title: 'Could not load the risk configuration', message: 'Please try again.' })
    } finally {
      setLoading(false)
    }
  }, [customerUuid, globalMode, clientId, canManage, scopeLoading])

  useEffect(() => { void load() }, [load])

  // Taxonomy clause types feed the builder selects; failure just leaves them free-text.
  useEffect(() => {
    if (scopeLoading || !canManage || (!globalMode && !clientId)) return
    let cancelled = false
    listClauseTypes(customerUuid)
      .then((types) => { if (!cancelled) setClauseTypes(types) })
      .catch(() => { if (!cancelled) setClauseTypes([]) })
    return () => { cancelled = true }
  }, [customerUuid, globalMode, clientId, canManage, scopeLoading])

  const effectiveConfig = useMemo(
    () => (scope ? parseBaselineConfig(scope.effectiveConfig) : null),
    [scope],
  )

  // The draft banner and pills compare the CURRENT edits (saved or not) against the effective
  // config — an unsaved edit is still an unpublished change.
  const draftDiff = useMemo(
    () => (config && effectiveConfig ? diffBaselineConfigs(effectiveConfig, config) : []),
    [config, effectiveConfig],
  )
  const hasDraftChanges = dirty || draftDiff.length > 0

  const nextVersion = (versions[0]?.version ?? 0) + 1

  const saveDraft = async (): Promise<boolean> => {
    if (!config) return false
    setSaving(true)
    try {
      const s = await updateBaselineDraft(customerUuid, JSON.stringify(config, null, 2))
      setScope(s)
      setDirty(false)
      return true
    } catch (e) {
      console.error('Save draft failed:', e)
      notifications.show({ color: 'red', title: 'Save failed', message: 'The backend rejected the config.' })
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDraft = async () => {
    if (saving) return
    if (await saveDraft()) {
      notifications.show({ color: 'green', title: 'Draft saved', message: 'Publish to apply it to risk evaluation.' })
    }
  }

  const openPublishConfirm = () => {
    if (publishing || saving) return
    setReevaluateOnPublish(false)
    setPublishConfirmOpen(true)
  }

  const handlePublish = async (reevaluate: boolean, note: string) => {
    if (publishing || saving) return
    setPublishConfirmOpen(false)
    setPublishing(true)
    try {
      if (dirty && !(await saveDraft())) return
      const v = await publishBaseline(customerUuid, note.trim() || null, reevaluate)
      notifications.show({
        color: 'green',
        title: `Published v${v.version}`,
        message: reevaluate
          ? 'Risk is being re-evaluated for every contract in this scope.'
          : 'Existing tiers keep their current version — re-analyse when you want them to catch up.',
      })
      await load()
    } catch (e) {
      console.error('Publish failed:', e)
      notifications.show({ color: 'red', title: 'Publish blocked', message: 'Validation failed or you lack admin access.' })
    } finally {
      setPublishing(false)
    }
  }

  const handleDiscardDraft = async () => {
    if (!scope?.effectiveConfig) return
    setDiscarding(true)
    try {
      const s = await updateBaselineDraft(customerUuid, scope.effectiveConfig)
      setScope(s)
      setConfig(parseBaselineConfig(s.draftConfig ?? s.effectiveConfig) ?? BASELINE_DEFAULTS)
      setDirty(false)
      notifications.show({ color: 'gray', message: 'Draft discarded — back to the effective config.' })
    } catch (e) {
      console.error('Discard draft failed:', e)
      notifications.show({ color: 'red', title: 'Discard failed', message: 'Please try again.' })
    } finally {
      setDiscarding(false)
    }
  }

  const handleRollback = async (version: number, reevaluate: boolean) => {
    try {
      const v = await rollbackBaseline(customerUuid, version, reevaluate)
      notifications.show({
        color: 'green',
        title: `Rolled back — published v${v.version}`,
        message: reevaluate
          ? 'Risk is being re-evaluated for every contract in this scope.'
          : 'Existing tiers keep their current version — re-analyse when you want them to catch up.',
      })
      await load()
    } catch (e) {
      console.error('Rollback failed:', e)
      notifications.show({ color: 'red', title: 'Rollback failed', message: 'Please try again.' })
    }
  }

  const header = (actions?: React.ReactNode) => (
    <PageHeader
      title="Risk"
      subtitle={`Two layers decide every tier for ${scopeName}: a deterministic rulebook, then an optional AI policy on top.`}
      actions={actions}
    />
  )

  if (scopeLoading || loading) {
    return (
      <div className="risk-admin">
        {header()}
        <Skeleton height={360} data-testid="risk-admin-loading" />
      </div>
    )
  }

  if (!canManage || (!globalMode && !clientId) || scope == null || config == null) {
    return (
      <div className="risk-admin">
        {header()}
        <Paper p="lg" withBorder>
          <Text c="dimmed">Select a Client (or switch to the global default) with admin access to manage risk.</Text>
        </Paper>
      </div>
    )
  }

  const publishItems: PublishChangeItem[] = draftDiff.map((d, i) => ({
    key: `${d.section}:${d.subject}:${i}`,
    label: d.sentence,
    detail: SECTION_LABEL[d.section],
  }))

  return (
    <div className="risk-admin">
      {header(
        <div className="ra-pills">
          {globalMode && <span className="ra-pill ra-pill--global">global default</span>}
          {!globalMode && scope.usesGlobalFallback && (
            <span className="ra-pill" title="This Client has no published config of its own yet">
              inherits global default
            </span>
          )}
          {scope.effectiveVersion != null && (
            <span className="ra-pill ra-pill--effective">effective v{scope.effectiveVersion}</span>
          )}
          {hasDraftChanges && <span className="ra-pill ra-pill--draft">unpublished draft</span>}
        </div>,
      )}

      <nav className="ra-tabs" aria-label="Risk configuration sections">
        {([
          ['baseline', 'Deterministic baseline'],
          ['ai-policy', 'AI policy'],
          ['versions', 'Versions'],
        ] as Array<[RiskTabId, string]>).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`ra-tab${tab === id ? ' ra-tab--active' : ''}`}
            onClick={() => setTab(id)}
            data-testid={`risk-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </nav>

      <div hidden={tab !== 'baseline'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          <BaselineTab
            config={config}
            canManage={canManage}
            clauseTypes={clauseTypes}
            usesGlobalFallback={scope.usesGlobalFallback}
            effectiveConfig={effectiveConfig}
            onChange={(next) => { setConfig(next); setDirty(true) }}
            rail={!globalMode && customerUuid != null
              ? <LiveExampleRail
                  customerUuid={customerUuid}
                  config={config}
                  effectiveVersionId={scope.effectiveVersionId}
                />
              : null}
          />
          <PublishBar
            testId="risk-admin-footer-baseline"
            alwaysShow
            count={draftDiff.length}
            what="the risk baseline"
            nextVersion={nextVersion}
            onReview={openPublishConfirm}
            onDiscard={hasDraftChanges ? () => { void handleDiscardDraft() } : undefined}
            discarding={discarding}
            extraActions={
              <button
                type="button"
                className="page-btn"
                onClick={() => { void handleSaveDraft() }}
                disabled={!canManage || !dirty || saving}
              >
                {saving ? 'Saving…' : 'Save draft'}
              </button>
            }
          />
        </div>
      </div>

      {/* Publishing and spending are two different decisions (Authorized LLM Spend): the version
          always freezes; the scope-wide re-evaluation — an LLM call per contract wherever a risk
          policy is published — runs only if this toggle is explicitly ticked. */}
      <PublishDialog
        opened={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        title={`Publish v${nextVersion}?`}
        changes={publishItems}
        emptyText={`No rule changes: publishes the current configuration as version ${nextVersion}, the config every future risk evaluation uses.`}
        onPublish={(note) => { void handlePublish(reevaluateOnPublish, note) }}
        busy={publishing}
        confirmLabel={reevaluateOnPublish ? 'Publish & re-evaluate' : 'Publish only'}
        confirmTestId="publish-confirm"
      >
        <Switch
          mt="md"
          checked={reevaluateOnPublish}
          onChange={(e) => setReevaluateOnPublish(e.currentTarget.checked)}
          label="Re-evaluate every contract in this scope now"
          description="Runs the full risk evaluation — including the AI risk policy where one is published, which spends LLM tokens per contract. Leave off to publish only; tiers catch up when you re-analyse them explicitly."
          data-testid="publish-reevaluate-toggle"
        />
      </PublishDialog>

      <div hidden={tab !== 'ai-policy'}>
        <RiskPolicyTab
          customerUuid={customerUuid}
          canManage={canManage}
          baselineConfig={globalMode ? null : config}
        />
      </div>

      <div hidden={tab !== 'versions'}>
        <RiskVersionsTab
          versions={versions}
          effectiveVersionId={scope.effectiveVersionId}
          usesGlobalFallback={scope.usesGlobalFallback}
          globalMode={globalMode}
          canManage={canManage}
          draftInfo={hasDraftChanges ? {
            editedAt: scope.draftUpdatedAt,
            basedOnVersion: scope.usesGlobalFallback ? null : scope.effectiveVersion,
            summary: summarizeBaselineDiff(draftDiff),
          } : null}
          onContinueEditing={() => setTab('baseline')}
          onDiscardDraft={handleDiscardDraft}
          onRollback={handleRollback}
        />
      </div>
    </div>
  )
}
